import Avatar from 'react-avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faPersonDigging } from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import moment from 'moment';
import BonusService from '../../services/bonus.service';
import { Table } from 'react-bootstrap';
import UserBonusesTable from '../../components/Bonuses/UserBonusesTable';
import { useTable, useExpanded } from 'react-table';
import { extractBonuses } from '../../utils/formatBonus';

const AdminBonusesPage = () => {
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const [userBonuses, setUserBonuses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    BonusService.GetAdminBonuses({ startDate, endDate })
      .then((res) => {
        const bonusesArr = res.data?.bonuses || [];
        const userBonusList = bonusesArr.map((item: any) => ({
          user: item.user,
          bonuses: extractBonuses(item),
        }));
        setUserBonuses(userBonusList);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const columns = useMemo(
    () => [
      {
        Header: '',
        id: 'expand',
        accessor: 'expand',
        width: 32,
        Cell: ({ row }: any) => (
          <td style={{ width: 32, textAlign: 'center', cursor: 'pointer' }} onClick={() => row.toggleRowExpanded()}>
            <FontAwesomeIcon icon={row.isExpanded ? faChevronDown : faChevronRight} />
          </td>
        ),
      },
      {
        Header: 'User Name',
        id: 'user',
        accessor: 'user',
        Cell: ({ data, row }: any) => {
          const user = data[row.id]?.user || {};
          const userId = user.userId || user.id;
          const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
          const { logoUrl } = data[row.id];
          return (
            <td>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  className="avatar img-fluid rounded-circle my-1"
                  alt={fullName}
                  width={24}
                  height={24}
                />
              ) : (
                <Avatar name={fullName} round={true} size="24" className="" />
              )}
              <a href={`/admin/user/${userId}`} target="_blank" rel="noopener noreferrer" className="mx-1 text-bold">
                {fullName}
              </a>
            </td>
          );
        },
      },
      {
        Header: 'Total Amount',
        id: 'amount',
        accessor: 'amount',
        Cell: ({ data, row }: any) => {
          const bonuses = data[row.id]?.bonuses || [];
          const sum = bonuses.reduce((acc: number, b: any) => acc + (Number(b.amount) || 0), 0);
          return <td className="fw-bold text-success">${sum.toLocaleString()}</td>;
        },
      },
      {
        Header: '# Bonuses',
        id: 'count',
        accessor: 'count',
        Cell: ({ data, row }: any) => {
          const bonuses = data[row.id]?.bonuses || [];
          return <td>{bonuses.length}</td>;
        },
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: 'status',
        Cell: () => <td></td>,
      },
    ],
    [],
  );

  const tableInstance = useTable({ columns, data: userBonuses }, useExpanded);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  return (
    <div className="container py-4">
      <h1 className="mb-4">All Bonuses</h1>
      <Card className="mb-4">
        <Card.Body>
          <div></div>
          <Row className="mb-3">
            <Col md={8}>
              <Form onSubmit={handleFilter}>
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                    />
                  </Col>
                  <Col md={4}>
                    <Button type="submit" variant="primary">
                      Filter
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
            <Col md={4} className="text-end">
              <a href="/manual-bonus" className="btn btn-primary d-inline-flex align-items-center">
                <FontAwesomeIcon icon={faPersonDigging} className="me-2" style={{ width: 16 }} />
                Manual Bonuses
              </a>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered className="table-sm">
              <thead>
                {headerGroups.map((headerGroup, idx) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={idx} className="table-header-gray">
                    {headerGroup.headers.map((column, index) => (
                      <th {...column.getHeaderProps()} key={index}>
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row: any, i: number) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={i}>
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell: any, idx: number) => (
                          <td {...cell.getCellProps()} key={idx}>
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                      {row.isExpanded && (
                        <tr>
                          <td colSpan={columns.length} className="bg-light">
                            <UserBonusesTable bonuses={row.original.bonuses} showTotalRow={true} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AdminBonusesPage;
